import { fn, hash } from '@ember/helper';
import { on } from '@ember/modifier';
import { LinkTo } from '@ember/routing';

import { MetricLibraryGridType } from 'webapp/enums/enums';

import didInsert from 'webapp/modifiers/did-insert';
import onWindowEvents from 'webapp/modifiers/on-window-events';
import type { Args as BaseArgs } from 'webapp/components/common/list/lazy-load';
import { CommonListLazyLoadBaseComponent } from 'webapp/components/common/list/lazy-load';
import CommonListLoadNextComponent from 'webapp/components/common/list/load-next';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import type { FilterSortMetricLibraryIndex } from 'webapp/components/metric-library/index';

import type FilterSort from 'webapp/models-custom/filter-sort/base';
import type FilterSortMetricLibrarySourceMetrics from 'webapp/models-custom/filter-sort/metric-library-source-metrics';
import type FilterSortMetricLibrarySourceTemplates from 'webapp/models-custom/filter-sort/metric-library-source-templates';
import type Metric from 'webapp/models-custom/metric';

export type MetricLibraryFilterSortType =
	| FilterSortMetricLibraryIndex
	| FilterSortMetricLibrarySourceMetrics
	| FilterSortMetricLibrarySourceTemplates
	| FilterSort<Metric>;

// the grid's own args, next to the lazy load's
interface GridArgs {
	title: string;
	type?: MetricLibraryGridType;
	hideCountBadge?: boolean;
}

// what the grid takes besides its filterSort (the grids wrapping it type their own)
export type Args = Omit<BaseArgs, 'filterSort' | 'filterExtraFn'> & GridArgs;

export default class MetricLibraryGridIndexComponent<
	FS extends MetricLibraryFilterSortType,
> extends CommonListLazyLoadBaseComponent<FS, GridArgs> {
	get hideCountBadge() {
		return this.itemsCount === 0 || this.args.hideCountBadge;
	}

	get isTypeCustom() {
		return this.args.type === MetricLibraryGridType.CUSTOM;
	}

	<template>
		<CommonListLoadNextComponent
			@allItems={{@filterSort.results}}
			@initialItemsShown={{this.initialItemsShown}}
			@nextItemsShown={{this.nextItemsShown}}
			@onVisibleItemsChanged={{this.updateVisibleItems}}
			as |_visibleItems loadNext|
		>
			<div
				class="ml-section {{this.classNames}}"
				{{didInsert (fn this.setupViewWithLoadNext loadNext)}}
				{{onWindowEvents this.updateGridOptions "resize" 200}}
			>
				<div class="ml-section__header">
					<div class="title-container">
						<div class="title">{{@title}}</div>
						{{#unless this.hideCountBadge}}
							<div class="ml-section__badge">{{this.itemsCountFormatted}}</div>
						{{/unless}}
					</div>
					{{#if this.isTypeCustom}}
						<div class="description">
							Custom metrics built with
							<LinkTo class="link--underline" @route="metrics">
								Metric builder
							</LinkTo>
							and
							<LinkTo class="link--underline" @route="datamanager.datasets">
								Datasets
							</LinkTo>
							<CommonUiIconComponent @name="Table" @size="sm" />
						</div>
					{{/if}}
				</div>
				<div class="ml-section__content">
					{{yield
						this.yieldedItems
						(hash
							loadNext=this.loadNext
							itemsPerRow=this.itemsPerRow
							hideSort=this.hideSort
							isNextLoadFinal=this.isNextLoadFinal
							areAllItemsLoaded=this.areAllItemsLoaded
						)
					}}
				</div>

				{{#unless this.areAllItemsLoaded}}
					<a class="ml-section__load-more" role="button" {{on "click" loadNext}}>
						Show
						{{#if this.isNextLoadFinal}}all{{else}}more{{/if}}...
					</a>
				{{/unless}}
			</div>
		</CommonListLoadNextComponent>
	</template>
}
